/**
 * IONLOOP Assistant — local question retrieval & ranking engine.
 *
 * searchKnowledge(query, options) converts a natural user question into
 * ranked relevant knowledge sections with scores, confidence, matched terms
 * and an explainable breakdown. Everything runs locally against the generated
 * IONLOOP knowledge index. No network, no embeddings, no LLM.
 */

var tokenizer = require('./tokenizer');
var normalizer = require('./query-normalizer');
var synonyms = require('./synonym-map');
var domainConcepts = require('./domain-concepts');
var scoring = require('./search-scoring');

var STOPWORDS = scoring.STOPWORDS;
var CATEGORY_SIGNALS = scoring.CATEGORY_SIGNALS;

// ---------------------------------------------------------------------------
// Intent classification (deterministic patterns)
// ---------------------------------------------------------------------------
function classifyIntent(normalizedQuery) {
    var q = String(normalizedQuery || '').trim();

    if (/\b(vs|versus)\b/.test(q) && /\b(or)\b/.test(q) || /\b(vs|versus)\b/.test(q)) {
        return 'comparison';
    }
    if (/^(what is|what are|whats|what's|what does|what do|define|what is an|what are the|what does the|explain what|tell me about|what is a|what is the)/i.test(q) ||
        /\b(means|meaning of|definition of)\b/i.test(q)) {
        return 'definition';
    }
    // Account-help must be checked before generic how-to ("how do I recover...").
    if (/\b(forgot|recover|recovery|reset|locked out|password)\b/i.test(q)) {
        return 'account-help';
    }
    if (/^(how do i|how do you|how can i|how to|how can you|how does one|steps to|guide to|setup|set up)/i.test(q)) {
        return 'how-to';
    }
    if (/^(where|find|open|navigate|locate|how do i find|go to|take me to|which page)/i.test(q)) {
        return 'navigation';
    }
    if (/^(can i|can you|does ionloop|does it|is there|do you have|is it possible|do you support|is there a|does the|does ionloop have|can it|is it\b|is [a-z]+ (available|supported|secure|safe))\b/i.test(q) ||
        /\b(supported|supports|support|available in)\b/i.test(q)) {
        return 'capability';
    }
    if (/^(how much|what .*cost|.*price\b)/i.test(q) || /\b(cost|price|pricing|subscription fee)\b/i.test(q)) {
        return 'capability';
    }
    if (/\b(contact|reach|support team|help desk|customer support|talk to someone)\b/i.test(q)) {
        return 'support';
    }
    if (/^(tell me about|what can i do with|what can you do|what does ionloop do|give me an overview|overview of|describe|what can i use|what can ionloop)/i.test(q) ||
        /^(what|which)\b.*\bavailable\b/i.test(q)) {
        return 'overview';
    }
    return 'general';
}

// ---------------------------------------------------------------------------
// Query analysis
// ---------------------------------------------------------------------------
function analyzeQuery(rawQuery, options, stats) {
    var original = typeof rawQuery === 'string' ? rawQuery.trim() : '';
    var normalized = normalizer.normalizeQuery(original);
    var tokens = tokenizer.tokenize(normalized);
    var meaningfulTokens = tokens.filter(function (tok) {
        return !STOPWORDS.has(tok);
    });

    // Stem meaningful tokens for plural/lemma recall.
    meaningfulTokens = meaningfulTokens.map(function (tok) {
        return tokenizer.stem(tok);
    });
    // Deduplicate after stemming.
    var seenTokens = Object.create(null);
    meaningfulTokens = meaningfulTokens.filter(function (tok) {
        if (seenTokens[tok]) { return false; }
        seenTokens[tok] = true;
        return true;
    });

    var negatives = [];
    var cleanedMeaningful = [];
    var i;
    for (i = 0; i < meaningfulTokens.length; i += 1) {
        var tok = meaningfulTokens[i];
        if (tok === 'not' || tok === 'without' || tok === 'except' || tok === 'excluding') {
            // Terms following a negation are treated as negative intent.
            var j = i + 1;
            while (j < meaningfulTokens.length) {
                negatives.push(meaningfulTokens[j]);
                j += 1;
            }
            break;
        }
        cleanedMeaningful.push(tok);
    }
    meaningfulTokens = cleanedMeaningful;

    var intent = classifyIntent(normalized);

    // Detect comparison entities ("spot vs margin").
    var comparisonEntities = [];
    var vsMatch = normalized.match(/\b([a-z0-9 ]+?)\s+(?:vs|versus)\s+([a-z0-9 ]+?)\b/);
    if (vsMatch) {
        comparisonEntities = [vsMatch[1].trim(), vsMatch[2].trim()];
        intent = 'comparison';
    }

    // Phrase dictionary from known multi-word titles/headings is provided via
    // stats.phraseSet (built at prepare time).
    var phrases = [];
    var phraseSet = stats.phraseSet;
    if (phraseSet) {
        tokenizer.phraseNGrams(tokens, 2, 3).forEach(function (ngram) {
            if (phraseSet[ngram]) {
                phrases.push({
                    text: ngram,
                    weight: 0.5 + Math.min(1.0, ngram.split(' ').length * 0.4),
                    length: ngram.split(' ').length
                });
            }
        });
        // Deduplicate phrases.
        var seenPhrases = Object.create(null);
        phrases = phrases.filter(function (p) {
            if (seenPhrases[p.text]) { return false; }
            seenPhrases[p.text] = true;
            return true;
        });
    }

    // Synonym expansion for meaningful tokens.
    var queryTerms = [];
    var seenTerms = Object.create(null);

    // NEW: concept + entity detection (deterministic semantic normalization).
    var concepts = domainConcepts.detectConcepts(normalized, meaningfulTokens);
    var entities = domainConcepts.detectEntities(normalized);

    function addQueryTerm(text, weight, kind, sourceTerm) {
        var key = text;
        if (!seenTerms[key]) {
            seenTerms[key] = true;
            queryTerms.push({ text: key, weight: weight, kind: kind, sourceTerm: sourceTerm || text });
        }
    }

// Expand each detected concept into weighted search terms.
    // Scale by concept confidence so suppressed concepts (e.g., pricing when
    // the context is BTC price) contribute very weak terms.
    concepts.forEach(function (concept) {
        var scale = Math.min(1, Math.max(0.1, concept.confidence));
        domainConcepts.expandConcept(concept.id).forEach(function (exp) {
            addQueryTerm(exp.term, exp.weight * scale, 'concept', concept.id);
        });
    });

    // Exact user tokens at full weight (may be morphological variants).
    meaningfulTokens.forEach(function (tok) {
        addQueryTerm(tok, 1.0, 'exact', tok);
    });

    // Fallback alias expansion for tokens not already covered by a concept.
    meaningfulTokens.forEach(function (tok) {
        var covered = queryTerms.some(function (qt) {
            return qt.sourceTerm === tok && (qt.kind === 'concept' || qt.kind === 'exact');
        });
        if (covered) { return; }
        var expanded = synonyms.expandTerm(tok);
        expanded.forEach(function (entry) {
            addQueryTerm(entry.term, entry.weight, entry.weight >= 1 ? 'exact' : 'synonym', tok);
        });
    });

    // Typo correction (conservative, vocabulary-only).
    meaningfulTokens.forEach(function (tok) {
        var corrected = scoring.correctTypoTerm(tok, stats);
        if (corrected && corrected !== tok && !seenTerms[corrected]) {
            seenTerms[corrected] = true;
            queryTerms.push({
                text: corrected,
                weight: 0.7,
                kind: 'typo',
                sourceTerm: tok,
                typoOf: tok
            });
        }
    });

    // Category signals derived from query tokens (counted per category so
    // strong single-category intent gets a larger boost).
    var categorySignals = new Map();
    Object.keys(CATEGORY_SIGNALS).forEach(function (category) {
        var signals = CATEGORY_SIGNALS[category];
        var count = 0;
        signals.forEach(function (signal) {
            var present = false;
            if (signal.indexOf(' ') !== -1) {
                present = normalized.indexOf(signal) !== -1;
            } else {
                present = meaningfulTokens.indexOf(signal) !== -1 ||
                    queryTerms.some(function (qt) { return qt.text === signal; });
            }
            if (present) {
                count += 1;
            }
        });
        if (count > 0) {
            categorySignals.set(category, count);
        }
    });

    // Off-topic heuristic: programming-language names without IONLOOP context.
    var offTopicLanguages = ['python', 'java', 'javascript', 'typescript', 'rust', 'golang', 'swift', 'kotlin', 'ruby', 'php', 'c#', 'c++', 'html', 'css', 'sql'];
    var hasProgramming = offTopicLanguages.some(function (lang) { return normalized.indexOf(lang) !== -1; });
    var hasIONLOOP = normalized.indexOf('ionloop') !== -1;
    var offTopicHint = hasProgramming && !hasIONLOOP;

    return {
        original: original,
        normalized: normalized,
        tokens: tokens,
        meaningfulTokens: meaningfulTokens,
        queryTerms: queryTerms,
        phrases: phrases,
        negatives: negatives,
        intent: intent,
        categorySignals: categorySignals,
        comparisonEntities: comparisonEntities,
        explicitCategory: (options && options.category) || null,
        strictCategory: !!(options && options.strictCategory),
        offTopicHint: offTopicHint,
        concepts: concepts,
        entities: entities
    };
}

// ---------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------
function computeConfidence(analysis, topResult, secondResult) {
    var conf = 0;

    if (analysis.meaningfulTokens.length === 0) {
        return 0.05;
    }

    if (!topResult) {
        return 0.0;
    }

    // Raw-score magnitude (calibrated against a strong title+heading match).
    var normalizedScore = Math.min(1, topResult.score / 50);
    conf += normalizedScore * 0.45;

    // Term coverage.
    conf += Math.min(1, topResult.termCoverage) * 0.25;

    // Title / heading match.
    if (topResult.matchedFields.indexOf('title') !== -1) { conf += 0.12; }
    if (topResult.matchedFields.indexOf('heading') !== -1) { conf += 0.10; }

    // Exact phrase match.
    if (topResult.phraseMatched && analysis.phrases.length > 0) { conf += 0.08; }

    // Gap between top and second result.
    var gap = 0;
    if (secondResult) {
        gap = (topResult.score - secondResult.score) / Math.max(1, topResult.score);
    } else {
        gap = 0.3;
    }
    conf += Math.min(0.1, Math.max(0, gap) * 0.1);

    // Synonym-only penalty.
    if (topResult.synonymOnly) {
        conf -= 0.15;
    }

    // Off-topic heuristic (e.g. programming-language questions).
    if (analysis.offTopicHint) {
        conf -= 0.35;
    }

    // Very low meaningful-token count can indicate a broad/ambiguous query.
    if (analysis.meaningfulTokens.length <= 1) {
        conf = Math.min(conf, 0.55);
    }

    return Math.max(0, Math.min(1, conf));
}

// ---------------------------------------------------------------------------
// Diversity / result collapsing
// ---------------------------------------------------------------------------
function applyDiversity(scored, maxPerDocument, limit) {
    var counts = {};
    var out = [];
    for (var i = 0; i < scored.length && out.length < limit; i += 1) {
        var entry = scored[i];
        var docId = entry.documentId;
        counts[docId] = counts[docId] || 0;
        if (counts[docId] < maxPerDocument) {
            counts[docId] += 1;
            out.push(entry);
        }
    }
    return out;
}

// ---------------------------------------------------------------------------
// Main API
// ---------------------------------------------------------------------------
function searchKnowledge(rawQuery, options) {
    options = options || {};
    var start = Date.now();

    var stats = getStats();
    if (!stats || stats.entries.length === 0) {
        return {
            query: typeof rawQuery === 'string' ? rawQuery : '',
            normalizedQuery: '',
            status: 'insufficient',
            confidence: 0,
            intent: 'general',
            results: [],
            elapsedMs: Date.now() - start
        };
    }

    var analysis = analyzeQuery(rawQuery, options, stats);

    if (analysis.meaningfulTokens.length === 0) {
        return {
            query: analysis.original,
            normalizedQuery: analysis.normalized,
            status: 'ambiguous',
            confidence: 0.1,
            intent: analysis.intent,
            suggestions: buildSuggestionsFromTop(stats.entries.slice(0, 5)),
            results: [],
            elapsedMs: Date.now() - start
        };
    }

    // Context boost (Phase 5): previous entity/category lightly boosts matching
    // sections without overriding explicit query wording.
    var context = options.context || null;
    var contextBoostMap = null;
    if (context && context.active) {
        contextBoostMap = buildContextBoostMap(context, stats);
    }

    var scored = [];
    for (var i = 0; i < stats.entries.length; i += 1) {
        var entry = stats.entries[i];
        var res = scoring.scoreSection(entry, analysis, stats);
        if (contextBoostMap) {
            var boost = contextBoostMap[entry.sectionId] || contextBoostMap[entry.documentId] || 0;
            res.score += boost;
            res.contextBoost = boost;
        }
        // Concept-to-canonical-topic boost: a section whose canonical topic
        // matches the query concept IS the authoritative source for that
        // concept, so it gets a strong semantic bonus that helps it outrank
        // long generic articles that merely mention the words.
        if (analysis.concepts && analysis.concepts.length && entry.canonicalTopic) {
            var conceptHit = analysis.concepts.some(function (c) { return c.id === entry.canonicalTopic; });
            if (conceptHit) {
                var conceptConf = Math.max.apply(null, analysis.concepts.filter(function (c) { return c.id === entry.canonicalTopic; }).map(function (c) { return c.confidence; }));
                res.score += 12 + Math.round(30 * conceptConf);
                res.conceptMatched = true;
            }
        }
        // Compute term coverage accounting for synonym expansion.
        var resolvedMeaningful = 0;
        analysis.meaningfulTokens.forEach(function (tok) {
            var expanded = synonyms.expandTerm(tok);
            var matched = expanded.some(function (exp) {
                return res.matchedTerms.indexOf(exp.term) !== -1;
            });
            if (matched) {
                resolvedMeaningful += 1;
            }
        });
        var termCoverage = analysis.meaningfulTokens.length > 0
            ? resolvedMeaningful / analysis.meaningfulTokens.length
            : 0;
        res.termCoverage = termCoverage;
        if (res.score > 0) {
            // Authority is a gentle tie-breaker: it scales the lexical score by
            // ~0.9–1.1 so relevance stays primary but better sources win ties.
            var authorityFactor = 0.9 + (entry.sourceAuthority || 80) * 0.002;
            res.score = res.score * authorityFactor;
            scored.push({
                documentId: entry.documentId,
                sectionId: entry.sectionId,
                title: entry.title,
                heading: entry.heading,
                category: entry.category,
                url: entry.url,
                sourceAuthority: entry.sourceAuthority || 80,
                score: res.score,
                breakdown: res.breakdown,
                matchedTerms: res.matchedTerms,
                matchedFields: res.matchedFields,
                termCoverage: res.termCoverage,
                exactCount: res.exactCount,
                anyExact: res.anyExact,
                phraseMatched: res.phraseMatched,
                synonymOnly: res.synonymOnly,
                conceptMatched: !!res.conceptMatched,
                contextBoost: res.contextBoost || 0
            });
        }
    }

    scored.sort(function (a, b) { return b.score - a.score; });

    var limit = options.limit || 6;
    var top = applyDiversity(scored, 2, limit);
    var topResult = top[0] || null;
    var secondResult = top[1] || null;
    var confidence = computeConfidence(analysis, topResult, secondResult);

    // Strict category filter (only when intentionally requested).
    if (analysis.strictCategory && analysis.explicitCategory) {
        top = top.filter(function (r) { return r.category === analysis.explicitCategory; });
    }

// Status determination.
    var status = 'answered';
    var coverageThreshold = analysis.meaningfulTokens.length >= 3 ? 0.4
        : (analysis.meaningfulTokens.length === 2 ? 0.3 : 0.2);
    var lowCoverage = topResult && topResult.termCoverage < coverageThreshold;

    if (!topResult || topResult.score <= 0) {
        status = 'insufficient';
    } else if (confidence < 0.3 && analysis.intent !== 'overview') {
        status = 'insufficient';
    } else if (lowCoverage && confidence < 0.4) {
        status = 'insufficient';
    } else if (analysis.meaningfulTokens.length === 1 && confidence < 0.5 && analysis.intent !== 'overview') {
        // One-word query: check if it resolves to a strong concept (topic
        // discovery). If the top result aligns with the concept, answer it.
        var singleTok = analysis.meaningfulTokens[0];
        var strongConcept = domainConcepts.strongConceptForToken(singleTok);
        if (strongConcept && topResult && topResult.category) {
            // Concept detected and top result exists — treat as answered.
            status = 'answered';
        } else {
            status = 'ambiguous';
        }
    }

    var suggestions = [];
    if (status === 'ambiguous') {
        suggestions = buildSuggestionsFromTop(top.length ? top : scored.slice(0, 5));
    }

    var elapsed = Date.now() - start;
    var response = {
        query: analysis.original,
        normalizedQuery: analysis.normalized,
        status: status,
        confidence: Math.round(confidence * 100) / 100,
        intent: analysis.intent,
        categorySignals: Array.from(analysis.categorySignals.keys()),
        matchedQueryTerms: analysis.meaningfulTokens,
        concepts: analysis.concepts,
        entities: analysis.entities,
        results: top,
        elapsedMs: elapsed
    };

    if (analysis.comparisonEntities && analysis.comparisonEntities.length) {
        response.comparisonEntities = analysis.comparisonEntities;
    }
    if (status === 'ambiguous') {
        response.suggestions = suggestions;
    }

    if (options.debug) {
        response.debug = {
            tokens: analysis.tokens,
            meaningfulTokens: analysis.meaningfulTokens,
            phrases: analysis.phrases,
            queryTerms: analysis.queryTerms,
            negatives: analysis.negatives,
            totalScored: scored.length,
            topBreakdown: topResult ? topResult.breakdown : null
        };
    }

    return response;
}

// ---------------------------------------------------------------------------
// Stats cache (prepared once)
// ---------------------------------------------------------------------------
var _stats = null;
var _statsDocuments = null;

function setKnowledgeIndex(documents) {
    _stats = scoring.prepareSearchIndex(documents);
    _stats.phraseSet = buildPhraseSet(documents);
    _statsDocuments = documents;
    return _stats;
}

function getStats() {
    return _stats;
}

function getDocuments() {
    return _statsDocuments || [];
}

/**
 * Build a lookup of known multi-word phrases from the index itself
 * (document titles + section headings) plus category labels.
 */
function buildPhraseSet(documents) {
    var phraseSet = {};
    function add(phrase) {
        var p = tokenizer.normalizePhrase(phrase);
        if (p && p.indexOf(' ') !== -1) {
            phraseSet[p] = true;
        }
    }
    (documents || []).forEach(function (doc) {
        add(doc.title);
        (doc.sections || []).forEach(function (section) {
            add(section.heading);
            (section.keywords || []).forEach(add);
        });
    });
    Object.keys(CATEGORY_SIGNALS).forEach(function (cat) {
        add(cat.replace(/-/g, ' '));
        CATEGORY_SIGNALS[cat].forEach(function (signal) {
            if (signal.indexOf(' ') !== -1) { add(signal); }
        });
    });
    return phraseSet;
}

/**
 * Build suggestions (topic candidates) from top scored entries.
 */
function buildSuggestionsFromTop(entries) {
    var seen = Object.create(null);
    var out = [];
    (entries || []).forEach(function (entry) {
        var title = (entry.title || '').trim();
        if (title && !seen[title]) {
            seen[title] = true;
            out.push(title.charAt(0).toUpperCase() + title.slice(1));
        }
    });
    return out.slice(0, 4);
}

function buildContextBoostMap(context, stats) {
    var boostMap = {};
    var boost = context.boost || 1.2;
    var terms = [];
    if (context.primaryEntity) { terms.push(context.primaryEntity); }
    (context.entities || []).forEach(function (e) { terms.push(e); });
    (context.recentTopics || []).forEach(function (t) { terms.push(t); });
    if (context.category) { terms.push(context.category.replace(/-/g, ' ')); }

    var termTokens = [];
    terms.forEach(function (t) {
        termTokens = termTokens.concat(tokenizer.tokenize(t));
    });

    stats.entries.forEach(function (entry) {
        var hits = 0;
        termTokens.forEach(function (tok) {
            if (entry.titleTokens.indexOf(tok) !== -1) { hits += 1; }
            if (entry.headingTokens.indexOf(tok) !== -1) { hits += 1; }
            if (entry.termFreq[tok]) { hits += 0.5; }
        });
        if (hits > 0) {
            boostMap[entry.sectionId] = boost * hits;
            boostMap[entry.documentId] = boost * hits;
        }
    });
    return boostMap;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
module.exports = {
    searchKnowledge: searchKnowledge,
    setKnowledgeIndex: setKnowledgeIndex,
    getStats: getStats,
    getDocuments: getDocuments,
    classifyIntent: classifyIntent,
    analyzeQuery: analyzeQuery,
    buildPhraseSet: buildPhraseSet
};
